import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  OperationalHoursAddForm,
  OperationalHoursAddInitialValues,
  OperationalHoursComponents,
  OperationalHoursAdditionalParams,
  OperationalHoursFormOptions,
  onOperationalHoursFormSaved,
  IOperationalHoursFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateOperationalHours, PermissionEnum, } from 'atlas-shared';
import { createOperationalHours, fetchOperationalHours, getOperationalHoursCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, useTimeIntervals, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateOperationalHours> {}

const OperationalHoursFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const time_intervals = useTimeIntervals();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateOperationalHours>>();

  const param_props: IOperationalHoursFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
    time_intervals: time_intervals.time_intervals,
  };
  const formLayout = useMemo(() => OperationalHoursAddForm(values, param_props, t), [values, organizations, time_intervals, t, abilities, ]);
  const options: IFormOptions = useMemo(() => OperationalHoursFormOptions(values || OperationalHoursAddInitialValues, param_props, t), [values, organizations, time_intervals, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateOperationalHours, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateOperationalHours) => save(() => createOperationalHours(values).then(
    res => onSaved ? onSaved(res, navigate) : onOperationalHoursFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchOperationalHours(+id)
        .then(({
          organization_id,
          title,
          timezone,
          __time_intervals,
        }) => {
          setCloned({
            organization_id,
            title,
            timezone,
            __time_intervals,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !time_intervals.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.OperationalHours, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={OperationalHoursComponents}
        additionalParams={OperationalHoursAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...OperationalHoursAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('OPERATIONAL_HOURS')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const OperationalHoursFormAddView = React.memo(withAdd(OperationalHoursFormAdd, getOperationalHoursCreateSchema, OperationalHoursAddInitialValues));
